import { Input, Modal, Button, List, message, Select, Collapse } from "antd";
import { PlusCircleFilled, SearchOutlined, EditOutlined, DeleteOutlined, UserOutlined, MailOutlined, PhoneOutlined, RightOutlined, DownOutlined, CaretRightOutlined, CaretDownOutlined } from "@ant-design/icons";
import React, { useState, useRef } from "react";

const { Option } = Select;

const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const DeveloperView = () => {
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [expandedDevs, setExpandedDevs] = useState([]); // Track expanded items
  const [devsList, setDevsList] = useState([
    { id: 1, firstName: "Raj Kumar", lastName: "Sen", email: "raj.sen@company.com" },
    { id: 2, firstName: "Siddharth", lastName: "Singh", email: "siddharth.singh@company.com" },
    { id: 3, firstName: "Gautam", lastName: "Kumar", email: "gautam.kumar@company.com" },
    { id: 4, firstName: "Ram Sahith", lastName: "Terli", email: "ram.terli@company.com" },
  ]);

  const [editingDev, setEditingDev] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    association: "",
    brokerage: "",
    phone: "",
  });

  const inputRef = useRef();

  // Toggle expanded state for a developer
  const toggleExpanded = (devId) => {
    setExpandedDevs((prev) => {
      if (prev.includes(devId)) {
        return prev.filter((id) => id !== devId);
      } else {
        return [...prev, devId];
      }
    });
  };

  // Modal functions for developer
  const displayAssigneeModal = () => {
    console.log("Plus icon clicked");
    setShowAssigneeModal(true);
    setEditingDev(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      association: "",
      brokerage: "",
      phone: "",
    });
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const { firstName, lastName, email } = formData;

    if (!firstName.trim()) {
      message.warning("First Name is required!");
      return false;
    }

    if (!lastName.trim()) {
      message.warning("Last Name is required!");
      return false;
    }

    if (!email.trim()) {
      message.warning("Email is required!");
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      message.warning("Please enter a valid email address!");
      return false;
    }

    return true;
  };

  const handleAddCustomDev = () => {
    if (!validateForm()) return;

    const { firstName, lastName, email, association, brokerage, phone } = formData;

    const capitalizedFirstName = capitalizeFirst(firstName.trim());
    const capitalizedLastName = capitalizeFirst(lastName.trim());
    const fullName = `${capitalizedFirstName} ${capitalizedLastName}`;

    // Check if developer already exists (excluding current editing dev)
    const duplicate = devsList.some((dev) => dev.email.toLowerCase() === email.toLowerCase() && (!editingDev || dev.id !== editingDev.id));

    if (duplicate) {
      message.warning("A developer with this email already exists!");
      return;
    }

    if (editingDev) {
      // Update existing developer
      setDevsList((prevList) =>
        prevList.map((dev) =>
          dev.id === editingDev.id
            ? {
                ...dev,
                firstName: capitalizedFirstName,
                lastName: capitalizedLastName,
                email: email.trim(),
                association: association || dev.association,
                brokerage: brokerage || dev.brokerage,
                phone: phone || dev.phone,
              }
            : dev
        )
      );
      message.success(`Developer ${fullName} updated successfully!`);
      setEditingDev(null);
    } else {
      // Create new developer with unique ID
      const newDev = {
        id: Date.now(),
        firstName: capitalizedFirstName,
        lastName: capitalizedLastName,
        email: email.trim(),
        association: association || "Unassigned",
        brokerage: brokerage || "N/A",
        phone: phone || "N/A",
      };
      setDevsList((prevList) => [...prevList, newDev]);
      message.success(`${fullName} added successfully!`);
    }

    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      association: "",
      brokerage: "",
      phone: "",
    });
    setShowAssigneeModal(false);
  };

  const handleEditDev = (dev) => {
    console.log("Edit icon clicked for:", `${dev.firstName} ${dev.lastName}`);
    setEditingDev(dev);
    setFormData({
      firstName: dev.firstName,
      lastName: dev.lastName,
      email: dev.email,
      association: dev.association,
      brokerage: dev.brokerage,
      phone: dev.phone,
    });
    setShowAssigneeModal(true);
  };

  const handleDeleteDev = (devId) => {
    Modal.confirm({
      title: "Delete Developer",
      content: "Are you sure you want to delete this developer?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        setDevsList((prevList) => prevList.filter((dev) => dev.id !== devId));
        setExpandedDevs((prev) => prev.filter((id) => id !== devId)); // Remove from expanded list
        message.success("Developer deleted successfully!");
      },
    });
  };

  const handleModalCancel = () => {
    setShowAssigneeModal(false);
    setEditingDev(null);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      association: "",
      brokerage: "",
      phone: "",
    });
  };

  // Filter developers based on search
  const filteredDevs = devsList.filter((dev) => `${dev.firstName} ${dev.lastName}`.toLowerCase().includes(searchKeyword.toLowerCase()) || dev.email.toLowerCase().includes(searchKeyword.toLowerCase()) || dev.association.toLowerCase().includes(searchKeyword.toLowerCase()));

  return (
    <div>
      {/* Search Bar - Centered properly */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          marginLeft: "620px",
          gap: "12px",
        }}
      >
        <Input
          placeholder="Type in keyword to search"
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          style={{
            width: 400,
            height: 40,
            borderRadius: 8,
            border: "1px solid #d9d9d9",
            backgroundColor: "#fafafa",
          }}
        />
        <PlusCircleFilled
          onClick={displayAssigneeModal}
          style={{
            color: "#275291",
            fontSize: "24px",
            cursor: "pointer",
            marginRight: "70px",
          }}
        />
      </div>

      {/* Developers List - Properly aligned */}
      <div
        style={{
          maxWidth: "100%",
          margin: "0 auto",
          padding: "0 20px",
        }}
      >
        <List
          dataSource={filteredDevs}
          renderItem={(dev) => {
            const isExpanded = expandedDevs.includes(dev.id);

            return (
              <div
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #f0f0f0",
                  borderBottom: "1px solid #f0f0f0",
                  marginBottom: "0",
                }}
              >
                {/* Main Developer Row */}
                <List.Item
                  style={{
                    padding: "16px 24px",
                    border: "none",
                    marginBottom: "0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  actions={[
                    <EditOutlined
                      key="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDev(dev);
                      }}
                      style={{
                        color: "#52c41a",
                        fontSize: "18px",
                        cursor: "pointer",
                        marginRight: "12px",
                      }}
                    />,
                    <DeleteOutlined
                      key="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDev(dev.id);
                      }}
                      style={{
                        color: "#ff4d4f",
                        fontSize: "18px",
                        cursor: "pointer",
                      }}
                    />,
                  ]}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleExpanded(dev.id)}
                  >
                    {/* Chevron Icon */}
                    {isExpanded ? (
                      <CaretDownOutlined
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          marginRight: "12px",
                          transition: "transform 0.2s",
                        }}
                      />
                    ) : (
                      <CaretRightOutlined
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          marginRight: "12px",
                          transition: "transform 0.2s",
                        }}
                      />
                    )}

                    {/* Developer Name */}
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#333",
                      }}
                    >
                      {`${dev.firstName} ${dev.lastName}`}
                    </span>
                  </div>
                </List.Item>

                {/* Expanded Details */}
                {isExpanded && (
                  <div
                    style={{
                      padding: "16px 24px 24px 50px", // Extra left padding to align with text
                      borderTop: "1px solid #f0f0f0",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {/* {Full Name} */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>Full Name:</span>
                        <span style={{ fontSize: "14px", color: "#666" }}>{`${dev.firstName} ${dev.lastName}`}</span>
                      </div>
                      {/* {First Name} */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>First Name:</span>
                        <span style={{ fontSize: "14px", color: "#666" }}>{dev.firstName}</span>
                      </div>
                      {/* {Last Name} */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>Last Name:</span>
                        <span style={{ fontSize: "14px", color: "#666" }}>{dev.lastName}</span>
                      </div>
                      {/* Email */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>Email:</span>
                        <span style={{ fontSize: "14px", color: "#666" }}>{dev.email}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          }}
          style={{
            backgroundColor: "#fff",
            border: "1px solid #f0f0f0",
            borderRadius: "8px",
          }}
        />

        {filteredDevs.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#999",
              backgroundColor: "#fff",
              border: "1px solid #f0f0f0",
              borderRadius: "8px",
            }}
          >
            {searchKeyword ? "No developers found matching your search." : "No developers added yet."}
          </div>
        )}
      </div>

      {/* Add/Edit Developer Modal - New Design */}
      <Modal title={null} visible={showAssigneeModal} onCancel={handleModalCancel} footer={null} width={600} style={{ top: 50 }} bodyStyle={{ padding: "40px" }} closable={true}>
        {/* Form */}
        <div style={{ marginTop: "20px" }}>
          {/* First Name and Last Name Row */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "24px" }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                <span style={{ color: "#ff4d4f" }}>*</span> First Name
              </label>
              <Input
                placeholder="Enter First Name"
                value={formData.firstName}
                onChange={(e) => handleFormChange("firstName", e.target.value)}
                style={{
                  height: "44px",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                <span style={{ color: "#ff4d4f" }}>*</span> Last Name
              </label>
              <Input
                placeholder="Enter Last Name"
                value={formData.lastName}
                onChange={(e) => handleFormChange("lastName", e.target.value)}
                style={{
                  height: "44px",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                }}
              />
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "500",
                color: "#333",
              }}
            >
              <span style={{ color: "#ff4d4f" }}>*</span> Email
            </label>
            <Input
              prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
              placeholder="Enter Email"
              value={formData.email}
              onChange={(e) => handleFormChange("email", e.target.value)}
              style={{
                height: "44px",
                borderRadius: "6px",
                border: "1px solid #d9d9d9",
              }}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="primary"
            onClick={handleAddCustomDev}
            disabled={!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()}
            style={{
              width: "100%",
              height: "48px",
              borderRadius: "24px",
              backgroundColor: "#1890ff",
              border: "none",
              fontSize: "16px",
              fontWeight: "500",
              color: "white",
            }}
          >
            {editingDev ? "Update" : "Add"}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default DeveloperView;
